/**
 * Custom Hook: useServiceTypes
 * Fetches service types from API with React Query caching
 * PERFORMANCE FIX: Converted from vanilla useEffect to React Query to prevent duplicate API calls
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api';

export interface ServiceType {
  id: string;
  nameTh: string;
  nameEn: string;
  description?: string;
  displayOrder: number;
  isActive: boolean;
}

interface UseServiceTypesResult {
  serviceTypes: ServiceType[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to fetch service types from API with caching
 * @param activeOnly - If true, only fetch active service types (default: true)
 * @returns Object containing serviceTypes array, loading state, error, and refetch function
 */
export const useServiceTypes = (activeOnly: boolean = true): UseServiceTypesResult => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['service-types', activeOnly],
    queryFn: async () => {
      const params = activeOnly ? '?active_only=true' : '';
      const response = await apiClient.get(`/api/service-types/${params}`);

      // Handle both possible response formats
      const data = response.data;
      const items = data.items || data.serviceTypes || [];

      // Sort by displayOrder
      return items.sort((a: ServiceType, b: ServiceType) =>
        a.displayOrder - b.displayOrder
      );
    },
    staleTime: 60 * 60 * 1000, // 1 hour - service types don't change often
    gcTime: 24 * 60 * 60 * 1000, // 24 hours (gcTime replaces cacheTime in React Query v5)
    retry: 2,
    retryDelay: 1000,
  });

  return {
    serviceTypes: data || [],
    loading: isLoading,
    error: error ? (error as Error).message || 'ไม่สามารถโหลดประเภทบริการได้' : null,
    refetch: () => { refetch(); }
  };
};

export default useServiceTypes;
